"use client";

import React from "react";
import { Lock, FilePen, ChartBar, FileText, Database } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
const workflowSteps = [{
  id: "login",
  title: "Request",
  description: "Open the Moveonix app and enter your destination. Our intuitive interface allows you to select your preferred vehicle class and view upfront pricing instantly.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658108/img/moveonix_request_ride.png",
  icon: Lock,
  badge: null
}, {
  id: "entry",
  title: "Match",
  description: "Our intelligent dispatch system pairs you with the nearest professional chauffeur. You'll receive real-time updates on their location and estimated arrival time.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658108/img/moveonix_driver_match.png",
  icon: FilePen,
  badge: null
}, {
  id: "analysis",
  title: "Journey",
  description: "Experience the pinnacle of comfort in our premium fleet. Every route is optimized for efficiency, ensuring you reach your destination safely and on schedule.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658108/img/moveonix_premium_journey.png",
  icon: ChartBar,
  badge: null
}, {
  id: "report",
  title: "Arrival",
  description: "Upon reaching your destination, payment is processed seamlessly through the app. Rate your experience to help us maintain our gold standard of service.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658108/img/moveonix_safe_arrival.png",
  icon: FileText,
  badge: null
}, {
  id: "storage",
  title: "Concierge",
  description: "Our dedicated support team and encrypted data infrastructure ensure every detail of your travel history is managed with the utmost discretion and care.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658108/img/moveonix_concierge_support.png",
  icon: Database,
  badge: "Premium Care"
}];
export default function AdministrativeWorkflow() {
  return <section data-section-id="224012" id="the-moveonix-experience" className="bg-white text-foreground py-24 px-6 md:px-12 lg:px-24 border-t border-border/20">
      <div className="max-w-6xl mx-auto">
        <div className="mb-16 max-w-2xl">
          <h2 className="font-default text-4xl md:text-5xl font-semibold tracking-tight mb-6 text-secondary">
            The Moveonix Experience
          </h2>
          <p className="font-default text-muted-foreground text-lg leading-relaxed">
            A seamless, highly refined journey designed for those who value precision and elegance. From the first tap to your final destination, we redefine modern mobility.
          </p>
        </div>

        <Tabs defaultValue="login" className="flex flex-col lg:flex-row gap-4 md:gap-6 lg:gap-24">
          <TabsList className="flex flex-col h-auto bg-transparent p-0 w-full lg:w-1/3 min-w-0 items-start justify-start gap-2 border-l border-border/50">
            {workflowSteps.map((step, i) => {
            const Icon = step.icon;
            return <TabsTrigger key={step.id} value={step.id} data-index={i} className="justify-start text-left px-6 py-5 border-l-4 border-transparent data-[state=active]:border-l-[#C9A15A] data-[state=active]:bg-accent/50 data-[state=active]:text-secondary data-[state=active]:scale-[1.04] text-muted-foreground hover:text-secondary hover:bg-muted transition-all duration-200 group rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between w-full gap-4">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-md bg-white border border-border group-data-[state=active]:border-[#C9A15A]/50 group-data-[state=active]:text-[#C9A15A] transition-colors shadow-sm">
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className="font-default font-medium text-base">
                        {step.title}
                      </span>
                    </div>
                    {step.badge && <Badge variant="outline" className="border-[#C9A15A]/30 text-[#C9A15A] text-[10px] uppercase tracking-widest font-mono">
                        {step.badge}
                      </Badge>}
                  </div>
                </TabsTrigger>;
          })}
          </TabsList>

          <div className="w-full lg:w-2/3 min-w-0">
            {workflowSteps.map((step, i) => <TabsContent key={step.id} value={step.id} data-index={i} className={`mt-0 outline-none focus-visible:ring-0 animate-fade-in duration-700 ${i === 0 ? "delay-0" : i === 1 ? "delay-100" : i === 2 ? "delay-200" : "delay-300"}`}>
                <div className="flex flex-col gap-8">
                  <div className="relative aspect-[16/9] w-full overflow-hidden rounded-lg border border-border bg-white shadow-xl flex items-center justify-center p-8">
                    <div className="absolute top-4 right-8 font-default text-8xl font-bold text-secondary/5 select-none">
                      0{i + 1}
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-br from-[#C9A15A]/5 to-transparent opacity-30" />
                    <img src={step.image} data-wvc-srcset={`${step.image}-wvc-srcset`} data-wvc-sizes={`${step.image}-wvc-sizes`} alt={`${step.title} illustration`} className="w-full h-full object-cover rounded-md opacity-90 hover:opacity-100 transition-all duration-500 scale-100 group-data-[state=active]:scale-105" loading="lazy" />
                  </div>
                  
                  <div className="max-w-xl">
                    <h3 className="font-default text-3xl font-medium mb-4 flex items-center gap-3 text-secondary">
                      {step.title}
                      {step.badge && <Badge variant="secondary" className="bg-[#C9A15A]/10 text-[#C9A15A] hover:bg-[#C9A15A]/20 border-transparent rounded-md">
                          {step.badge}
                        </Badge>}
                    </h3>
                    <p className="font-default text-muted-foreground text-lg leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </TabsContent>)}
          </div>
        </Tabs>
      </div>
    </section>;
}